import React, { useState } from "react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import BottomNav from "../components/BottomNav";
import { Eye, EyeOff } from "lucide-react";

const SendPage = () => {
  const { user, refreshUser, authAxios } = useAuth();
  const { t } = useLanguage();
  const [recipient, setRecipient] = useState("");
  const [amount, setAmount] = useState("");
  const [withdrawalPin, setWithdrawalPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!recipient || !amount) {
      toast.error("Please fill all fields");
      return;
    }
    
    if (!withdrawalPin || withdrawalPin.length !== 6) {
      toast.error("Enter 6-digit withdrawal PIN");
      return;
    }
    
    const parsedAmount = parseFloat(amount);
    if (parsedAmount <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    setLoading(true);
    try {
      const api = authAxios();
      const res = await api.post(`/wallet/send-usdt`, {
        recipient,
        amount: parsedAmount,
        withdrawal_pin: withdrawalPin
      });
      toast.success(`USDT sent! ${res.data.ltg_staked} LTG auto-staked for ${res.data.recipient_name}`);
      setRecipient("");
      setAmount("");
      setWithdrawalPin("");
      refreshUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setLoading(false);
    }
  };

  const availableUSDT = user ? (user.usdt_balance || 0) : 0;

  return (
    <div className="min-h-screen pb-24 p-4" data-testid="send-page">
      <h1 className="font-manrope font-bold text-2xl mb-4">Send USDT</h1>

      {/* Account Not Activated Warning */}
      {user && !user.is_activated && (
        <div className="rounded-xl p-4 mb-4 bg-red-500/10 border border-red-500/30">
          <p className="text-red-400 font-semibold">Account Not Activated</p>
          <p className="text-red-300 text-sm mt-1">
            Deposit ${(user.activation_required || 50).toFixed(0)} to unlock Send feature
          </p>
        </div>
      )}

      {/* Balance Display */}
      <div className="rounded-xl p-4 mb-4 bg-green-900/30 border border-green-500/30">
        <p className="text-sm text-muted-foreground">Available USDT Balance</p>
        <p className="font-manrope font-bold text-2xl text-green-400">
          ${availableUSDT.toFixed(4)} USDT
        </p>
      </div>

      <div className="bg-card border border-border rounded-xl p-4 space-y-4">
        <div>
          <label className="text-muted-foreground text-sm mb-2 block">{t("recipient")}</label>
          <input
            type="text"
            placeholder="Phone / Email / Wallet Address"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
            className="input-dark"
            data-testid="send-recipient-input"
          />
        </div>

        <div>
          <label className="text-muted-foreground text-sm mb-2 block">
            {t("amount")} (USDT)
          </label>
          <input
            type="number"
            placeholder="Enter USDT amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="input-dark"
            data-testid="send-amount-input"
          />
          {amount && parseFloat(amount) > 0 && (
            <p className="text-xs text-green-400 mt-2">
              = {(parseFloat(amount) * 1000).toLocaleString()} LTG will be staked for recipient
            </p>
          )}
        </div>

        <div>
          <label className="text-muted-foreground text-sm mb-2 block">
            🔐 6-Digit Withdrawal PIN
          </label>
          <div className="relative">
            <input
              type={showPin ? "text" : "password"}
              placeholder="Enter 6-digit withdrawal PIN"
              value={withdrawalPin}
              onChange={(e) => setWithdrawalPin(e.target.value.replace(/\D/g, ""))}
              maxLength={6}
              className="input-dark w-full pr-10"
              data-testid="send-pin-input"
            />
            <button
              type="button"
              onClick={() => setShowPin(!showPin)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            >
              {showPin ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button
          onClick={handleSend}
          disabled={loading || withdrawalPin.length !== 6 || !user?.is_activated}
          className="w-full py-3.5 rounded-full text-lg font-bold transition-all bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
          data-testid="send-submit-btn"
        >
          {!user?.is_activated ? "Activate Account to Send" : loading ? "Sending..." : "Send USDT"}
        </button>
      </div>

      {/* Info Box */}
      <div className="mt-4 bg-card/50 border border-border rounded-xl p-4">
        <h3 className="font-bold text-sm mb-2">ℹ️ How USDT Transfer Works</h3>
        <ul className="text-xs text-muted-foreground space-y-1">
          <li>• Send USDT to any user on the platform</li>
          <li>• Recipient's USDT will be auto-staked as LTG</li>
          <li>• Recipient starts earning 1% daily immediately</li>
          <li>• Your referrer gets 10% bonus on this transfer</li>
        </ul>
      </div>

      <BottomNav />
    </div>
  );
};

export default SendPage;
